import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthUser, LoginCredentials, AuthContextType } from '../types';
import { authApi } from '../services/authApi';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await authApi.getProfile();
      if (response.success) {
        setUser(response.data);
      } else {
        // Token is invalid, remove it
        console.warn('Invalid token detected during auth check, clearing localStorage');
        localStorage.removeItem('accessToken');
        setUser(null);
      }
    } catch (error: any) {
      console.error('Auth check failed:', error);
      // Clear invalid token and user state
      localStorage.removeItem('accessToken');
      setUser(null);
      
      // If it's a 401 error, the API interceptor will handle the redirect
      // For other errors, we just clear the auth state
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      console.log('AuthContext: Starting login process');
      setIsLoading(true);
      const response = await authApi.login(credentials);
      console.log('AuthContext: Login response:', response);
      
      if (response.success) {
        console.log('AuthContext: Login successful, setting user and token');
        localStorage.setItem('accessToken', response.data.accessToken);
        setUser(response.data.user);
        return true;
      }
      
      console.log('AuthContext: Login failed - response not successful');
      return false;
    } catch (error) {
      console.error('AuthContext: Login failed with error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      setUser(null);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const response = await authApi.refreshToken();
      if (response.success) {
        localStorage.setItem('accessToken', response.data.accessToken);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const hasPermission = useCallback((module: string, action: string): boolean => {
    if (!user) return false;
    
    // Superadmin has all permissions
    if (user.is_superadmin) return true;
    
    // Check user permissions
    const permission = user.permissions?.find(p => p.module_name === module);
    if (!permission) return false;
    
    const actionKey = `can_${action}` as keyof typeof permission;
    return !!permission[actionKey];
  }, [user]);

  const hasAnyPermission = useCallback((module: string): boolean => {
    if (!user) return false;
    
    // Superadmin has all permissions
    if (user.is_superadmin) return true;
    
    // Check if user has any permission for the module
    return (
      hasPermission(module, 'create') ||
      hasPermission(module, 'read') ||
      hasPermission(module, 'update') ||
      hasPermission(module, 'delete')
    );
  }, [user, hasPermission]);

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
    hasPermission,
    hasAnyPermission,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};